<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': isDisplayed,
    }"
    :data-test="`session-slide-${index}`"
  >
    <template #text>
      <!--      <SlideText :text="prismicSlice.primary.type" />-->
      <CmsPreStrengthMappingCapture
        v-if="prismicSlice.primary.type === 'pre-strength-mapping'"
        @on-submit-data="onSubmitData"
        data-test="pre-strength-mapping-data-capture-slide"
      />
      <CmsPostStrengthMappingCapture
        v-else-if="prismicSlice.primary.type === 'post-strength-mapping'"
        @on-submit-data="onSubmitData"
        data-test="post-strength-mapping-data-capture-slide"
      />
    </template>
    <template #visual>
      <VisualPositioningCanvas v-if="!!image">
        <CmsImg ref="visual" class="slide-image" :src="image" />
      </VisualPositioningCanvas>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useToggle } from "../../../composables/useToggle";
import CmsImg from "../cms-img.vue";
import CmsPreStrengthMappingCapture from "../cms-pre-strength-mapping-capture.vue";
import CmsPostStrengthMappingCapture from "../cms-post-strength-mapping-capture.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import SessionSlide from "../../sessions/session-slide.vue";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import { CmsSessionSlideWithDataCapture } from "../../../services/cms/cms-type-definitions";

const props = defineProps<{ slice: PrismicSliceWithData; index: number }>();

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const prismicSlice = computed<CmsSessionSlideWithDataCapture>(() => {
  return props.slice.prismicSlice as CmsSessionSlideWithDataCapture;
});

const image = computed(() => prismicSlice.value.primary.image);

function onSubmitData(slideType: "preStrengthMapping" | "postStrengthMapping") {
  // Required to make sure the slide stays hidden before completing strength mapping in the remapping session.
  if (slideType === "postStrengthMapping") cmsSessionStore.hasFinishedTeamTagging = true;
  cmsSessionStore.updateSectionStatus(props.slice.section, true);
  // scroll to bottom of page
  nextTick(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  });
}

watch(
  () => cmsSessionStore.sectionsToDisplay,
  () => {
    if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(props.slice.section)) {
      toggleIsDisplayed();
    }
  },
  { immediate: true, deep: true },
);

// These 3 refs need to be exposed so that cms-session can apply animations to them
const visual = ref<InstanceType<typeof CmsImg> | null>(null);
const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

defineExpose({
  visualDiv,
  visual,
  slide,
});
</script>
